import i18n from "i18next";
import { isProblemWithCode, UiErrorException, type UiError } from "../../errors/uiError";
import { servedBuildIsNewer } from "../../serverStatus/appBuild";
import { apiUrl, resolverUrl } from "./apiConfiguration";
import { JsonContractError } from "./jsonDecoding";
import { PROBLEM_JSON_MEDIA_TYPE } from "./problemDetails";
import { uiErrorFromFailedResponse } from "./problemResponses";
import { csrfHeaderForRequest, reportEndedSession, type HttpMethod } from "./sessionCredentials";

export type { HttpMethod } from "./sessionCredentials";

/** A request body: a JSON value, or a file sent as it is (for example JSON Lines). */
export type RequestBody =
  | { readonly kind: "json"; readonly value: unknown }
  | { readonly kind: "file"; readonly content: Blob; readonly contentType: string };

export interface ApiRequest {
  readonly method: HttpMethod;
  readonly accept?: string;
  /** Resolver paths live beside the API on the same server. */
  readonly resolver?: boolean;
  /** A path of the DMW API, such as `/health/ready`, with its query if any. */
  readonly path: `/${string}`;
  readonly body?: RequestBody;
  readonly signal?: AbortSignal;
}

export interface JsonRequest<TResponse> extends ApiRequest {
  /** Checks the response against the API contract and returns it typed. */
  readonly decodeResponse: (body: unknown) => TResponse;
}

const ACCEPTED_MEDIA_TYPES = `application/json, ${PROBLEM_JSON_MEDIA_TYPE}`;

export function jsonBody(value: unknown): RequestBody {
  return { kind: "json", value };
}

/**
 * `path?name=value&…`, leaving out parameters whose value is `null`. A list repeats the
 * parameter once per value (`corpus=a&corpus=b`); an empty list leaves it out.
 */
export function withQuery(
  path: `/${string}`,
  parameters: Readonly<Record<string, string | number | readonly string[] | null>>,
): `/${string}` {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(parameters)) {
    if (isValueList(value)) {
      for (const each of value) query.append(name, each);
    } else if (value !== null) {
      query.set(name, String(value));
    }
  }
  const queryText = query.toString();
  return queryText === "" ? path : `${path}?${queryText}`;
}

// `Array.isArray` narrows to `any[]` and leaves a readonly array in the other branch.
function isValueList(value: string | number | readonly string[] | null): value is readonly string[] {
  return Array.isArray(value);
}

/**
 * Send one request to the DMW API and return its decoded JSON body.
 *
 * Rejects with `UiErrorException` for every failure: no answer (`serverUnreachable`), a
 * problem details answer (`problem`), or an answer that breaks the contract
 * (`unexpectedResponse`). An aborted request rejects with the browser's `AbortError`.
 */
export async function requestJson<TResponse>(request: JsonRequest<TResponse>): Promise<TResponse> {
  const response = await send(request);

  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await outOfStepChecked({ kind: "unexpectedResponse", status: response.status, reason: "invalidJson" });
  }

  try {
    return request.decodeResponse(body);
  } catch (error) {
    if (error instanceof JsonContractError) {
      console.error(`Response of ${request.method} ${request.path} breaks the API contract.`, error);
      throw await outOfStepChecked({ kind: "unexpectedResponse", status: response.status, reason: "contractViolation" });
    }
    throw error;
  }
}

/** Answers that mean the request or the answer doesn't fit the API this tab was built for. */
const CONTRACT_PROBLEM_CODES: ReadonlySet<string> = new Set(["route_not_found", "request_validation_failed", "malformed_request"]);

/**
 * A failure that a tab on an older build would meet. When a newer build of the app is served,
 * the tab is the one out of step, so the error says to reload (F89); otherwise it stays as it is.
 */
async function outOfStepChecked(error: UiError): Promise<UiErrorException> {
  // A failure without problem details came from something in between (a proxy's 429 or 502),
  // not from the API this tab was built for: it keeps its own message.
  const contractBroken =
    (error.kind === "unexpectedResponse" && error.reason !== "notProblemDetails") ||
    (error.kind === "problem" && CONTRACT_PROBLEM_CODES.has(error.problem.code));
  return new UiErrorException(contractBroken && (await servedBuildIsNewer()) ? { kind: "appOutdated" } : error);
}

/** Send a request whose success answer has no body (204), such as logging out. */
export async function requestWithoutContent(request: ApiRequest): Promise<void> {
  await send(request);
}

/** Read a downloadable representation through the same session and error handling. */
export async function requestBlob(request: ApiRequest): Promise<Blob> {
  return (await send(request)).blob();
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/** Send the request with the session's CSRF header; reject unless the answer is a success. */
async function send(request: ApiRequest): Promise<Response> {
  const headers = new Headers({ Accept: request.accept ?? ACCEPTED_MEDIA_TYPES });
  // The person's language: content reads give names and descriptions in it (processes.md P-21).
  if (i18n.resolvedLanguage !== undefined) {
    headers.set("Accept-Language", i18n.resolvedLanguage);
  }
  const csrfHeader = csrfHeaderForRequest(request.method);
  if (csrfHeader !== null) {
    headers.set(...csrfHeader);
  }
  let body: BodyInit | null = null;
  if (request.body?.kind === "json") {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(request.body.value);
  } else if (request.body?.kind === "file") {
    headers.set("Content-Type", request.body.contentType);
    body = request.body.content;
  }

  let response: Response;
  try {
    response = await fetch(request.resolver === true ? resolverUrl(request.path) : apiUrl(request.path), {
      method: request.method,
      headers,
      body,
      credentials: "same-origin",
      signal: request.signal ?? null,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    // `fetch` rejects only when no HTTP answer arrived at all.
    throw new UiErrorException({ kind: "serverUnreachable" });
  }

  if (!response.ok) {
    const error = await uiErrorFromFailedResponse(response);
    if (isProblemWithCode(error, "authentication_required")) {
      reportEndedSession();
    }
    throw await outOfStepChecked(error);
  }
  return response;
}
